'use client';

import Image from 'next/image';
import StickyNavbar from '@/components/silicon/StickyNavbar';
import { useBootstrap, useThemeInit } from '@/lib/utils/clientUtils';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { useEffect } from 'react';
import { useSession, signIn, signOut } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useAnalytics } from '@/hooks/useAnalytics';

// Declare Bootstrap for window object
declare global {
  interface Window {
    bootstrap: any;
  }
}

const Header: React.FC = () => {
  // Initialize Bootstrap functionality (like dropdowns, offcanvas, etc.)
  useBootstrap();
  
  // Initialize theme functionality (like sticky navbar behavior)
  useThemeInit();

  const t = useTranslations('common');
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const { trackEvent, isEnabled } = useAnalytics();
  
  const loading = status === 'loading';
  const user = session?.user;

  // Function to manually close the offcanvas menu
  const closeOffcanvas = () => {
    const offcanvasElement = document.getElementById('navbarNav');
    if (offcanvasElement) {
      // Only remove the 'show' class - let Bootstrap handle the rest
      offcanvasElement.classList.remove('show');
      
      // Remove backdrop with a slight delay to ensure smooth transition
      setTimeout(() => {
        const backdrop = document.querySelector('.offcanvas-backdrop');
        if (backdrop) {
          backdrop.classList.remove('show');
          // Remove backdrop after transition completes
          setTimeout(() => {
            if (backdrop.parentNode) {
              backdrop.parentNode.removeChild(backdrop);
            }
          }, 150);
        }
        
        // Reset body styles
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open');
      }, 50);
    }
  };

  // Add event listeners for navigation links
  useEffect(() => {
    const setupNavClose = () => {
      // Get all navigation links in the offcanvas
      const navLinks = document.querySelectorAll('#navbarNav a, #navbarNav .btn');
      
      navLinks.forEach(link => {
        // Skip dropdown toggles
        if (link.classList.contains('dropdown-toggle')) return;
        
        const handleClick = () => {
          // Small delay to allow navigation to start
          setTimeout(() => {
            closeOffcanvas();
          }, 100);
        };
        
        link.addEventListener('click', handleClick);
        
        // Store the handler for cleanup
        (link as any)._offcanvasHandler = handleClick;
      });
    };

    // Setup after a delay to ensure DOM is ready
    const timer = setTimeout(setupNavClose, 500);

    return () => {
      clearTimeout(timer);
      // Cleanup event listeners
      const navLinks = document.querySelectorAll('#navbarNav a, #navbarNav .btn');
      navLinks.forEach(link => {
        if ((link as any)._offcanvasHandler) {
          link.removeEventListener('click', (link as any)._offcanvasHandler);
          delete (link as any)._offcanvasHandler;
        }
      });
    };
  }, []);



  // Handle Spotify login click with NextAuth
  const handleSpotifyLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    const callbackUrl = pathname || '/';
    signIn('spotify', { callbackUrl });
  };
  
  // Handle sign out
  const handleSignOut = (e: React.MouseEvent) => {
    e.preventDefault();
    signOut();
  };

  // Handle mobile navigation toggle
  const handleMobileNavToggle = () => {
    if (isEnabled) {
      trackEvent('mobile_nav_opened', {
        source: 'header',
        page_url: pathname,
        device_type: 'mobile',
        interaction_type: 'click'
      });
    }
  };

  return (
    <StickyNavbar scrollOffset={50} className="header navbar navbar-expand-lg shadow-sm position-absolute py-3" data-bs-theme="dark">
      <div className="container px-3">
        <Link href="/" className="navbar-brand pe-3">
          <Image 
            src="/assets/img/logo.svg" 
            width={172} 
            height={50}
            alt={t('global.title')}
            className="d-block"
            style={{ width: '172px', height: '50px' }}
            unoptimized
          />
        </Link>
        
        <div id="navbarNav" className="offcanvas offcanvas-end">
          <div className="offcanvas-header border-bottom" style={{ borderColor: '#353945 !important' }}>
            <h5 className="offcanvas-title">{t('global.header.mainMenu')}</h5>
            <button 
              type="button" 
              className="btn-close" 
              data-bs-dismiss="offcanvas" 
              aria-label="Close">
            </button>
          </div>
          <div className="offcanvas-body">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item d-lg-none">
                <Link href="/" className="nav-link fw-500">
                  {t('global.header.navigation.home')}
                </Link>
              </li>
              <li className="nav-item">
                <Link href="/about/what-is-musicmagic-io" className="nav-link fw-500">
                  {t('global.header.navigation.howItWorks')}
                </Link>
              </li>
              <li className="nav-item dropdown">
                <Link 
                  href="#" 
                  className="nav-link dropdown-toggle fw-500" 
                  data-bs-toggle="dropdown" 
                  data-bs-auto-close="true"
                  aria-expanded="false"
                  onClick={(e) => e.preventDefault()}
                >
                  {t('global.header.navigation.discoverPlaylists')}
                </Link>
                <ul className="dropdown-menu">
                  <li>
                    <Link href="/playlists" className="dropdown-item">
                      {t('global.header.navigation.viewAllPlaylists')}
                    </Link>
                  </li>
                  <li>
                    <Link href="/playlists/categories" className="dropdown-item">
                      {t('global.header.navigation.playlistCategories')}
                    </Link>
                  </li>
                  <li>
                    <Link href="/playlists/tags" className="dropdown-item">
                      {t('global.header.navigation.playlistTags')}
                    </Link>
                  </li>
                  <li>
                    <Link href="/playlists/curated-by" className="dropdown-item">
                      {t('global.header.navigation.playlistCurators')}
                    </Link>
                  </li>
                  <li>
                    <Link href="/playlists/featuring" className="dropdown-item">
                      {t('global.header.navigation.featuredArtists')}
                    </Link>
                  </li>
                  <li>
                    <Link href="/playlists/genres" className="dropdown-item">
                      {t('global.header.navigation.featuredGenres')}
                    </Link>
                  </li>
                </ul>
              </li>
              <li className="nav-item">
                <Link href="/playlist-generator" className="nav-link fw-500">
                  {t('global.header.navigation.buildPlaylist')}
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Mobile Spotify Button - Fixed at bottom */}
          <div className="offcanvas-header border-top d-lg-none" style={{ borderColor: '#353945 !important' }}>
            {loading ? (
              <div className="btn btn-gradient w-100 disabled">
                <i className="bx bx-loader-alt bx-spin fs-4 lh-1 me-1"></i>
                Loading...
              </div>
            ) : user ? (
              <Link 
                href="/my/spotify" 
                className="btn btn-gradient w-100"
              >
                <i className="bx bxl-spotify fs-4 lh-1 me-1"></i>
                My Spotify
              </Link>
            ) : (
              <button 
                onClick={handleSpotifyLogin}
                className="btn btn-gradient w-100"
              >
                <i className="bx bxl-spotify fs-4 lh-1 me-1"></i>
                {t('global.header.navigation.connectSpotify')}
              </button>
            )}
          </div>
        </div>
        
        {/* Desktop Spotify Button */}
        <div className="d-none d-lg-flex align-items-center ms-auto">
          {loading ? (
            <div className="btn btn-gradient btn-sm disabled">
              <i className="bx bx-loader-alt bx-spin fs-5 lh-1 me-1"></i>
              Loading...
            </div>
          ) : user ? (
            <div className="dropdown">
              <button 
                className="btn btn-gradient btn-sm dropdown-toggle" 
                type="button" 
                data-bs-toggle="dropdown" 
                aria-expanded="false"
              >
                <i className="bx bxl-spotify fs-5 lh-1 me-2"></i>
                {user.image && (
                  <Image 
                    src={user.image} 
                    alt={user.name || 'User'} 
                    className="rounded-circle me-2"
                    width={20}
                    height={20}
                  />
                )}
                <span className="d-none d-xl-inline">{user.name || 'My Profile'}</span>
                <i className="bx bx-user fs-5 lh-1 d-xl-none"></i>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <Link href="/my/spotify" className="dropdown-item">
                    <i className="bx bx-user me-2"></i>
                    My Spotify
                  </Link>
                </li>
                <li>
                  <Link href="/my/playlists" className="dropdown-item">
                    <i className="bx bx-music me-2"></i>
                    My Playlists
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button 
                    onClick={handleSignOut}
                    className="dropdown-item w-100 text-start border-0 bg-transparent"
                  >
                    <i className="bx bx-log-out me-2"></i>
                    Sign Out
                  </button>
                </li>
              </ul>
            </div>
          ) : (
            <button 
              onClick={handleSpotifyLogin}
              className="btn btn-gradient btn-sm"
            >
              <i className="bx bxl-spotify fs-5 lh-1 me-1"></i>
              {t('global.header.navigation.connectSpotify')}
            </button>
          )}
        </div>
        
        {/* Mobile menu toggle */}
        <button
          type="button"
          className="navbar-toggler ms-3"
          data-bs-toggle="offcanvas"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label={t('global.header.toggleNavigation')}
          onClick={handleMobileNavToggle}
        >
          <span className="navbar-toggler-icon"></span>
        </button>
      </div>
    </StickyNavbar>
  );
};

export default Header;